import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store/store';
import {
  SalesOrderInfo,
  fetchSalesOrders,
  selectSalesOrders,
  selectSalesOrdersStatus,
} from '../store/slices/salesOrders';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  IAccountInfo,
  IPerson,
  selectAccountInfo,
  selectBusinessPartnerId,
  selectDirtyState,
  selectPersonLoggedIn,
  selectUserLocation,
  selectUserRole,
  setDirtyState,
} from '../store/slices/auth';

import {
  ConsultationFinding,
  selectConsultationFindingStatus,
  selectConsultationFindings,
} from '../store/slices/consultationFindings';
import {
  IWorkOrderService,
  createWorkOrderService,
  selectCreatedWorkOrderService,
  updateStatusOfConsultation,
} from '../store/slices/workOrderServiceConsultation';
import {
  ConsultationRecommendation,
  selectConsultationRecommendationStatus,
  selectConsultationRecommendations,
} from '../store/slices/consultationRecommendations';
import {
  ConsultationNote,
  selectConsultationNoteStatus,
  selectConsultationNotes,
} from '../store/slices/consultationNotes';
import { IDoctor, fetchDoctors, selectDoctors } from '../store/slices/doctors';
import {
  ConsultationInvestigation,
  fetchConsultationInvestigations,
  selectConsultationInvestigationStatus,
  selectConsultationInvestigations,
} from '../store/slices/consultationInvestigations';
import { ConsultationTypes } from '../enums/enums';
import { EditorState } from 'draft-js';
import { findProductWithTaIdAndTransactionId } from '../helpers';
import { IDigitalHealthProfile } from '../components/UI/SelectedSalesOrder/DigitalHealthProfileTab';
import { useTranslation } from '../hooks/useTranslations';
import { NURSE_USER_ROLE } from '../constants/userRolesConstants';
interface Props {
  children: React.ReactNode;
}

// type StateHandler = (key: string, state: string) => void;

type TabBar = {
  value: number;
  text: string;
  isVisible: boolean;
};

interface ConsultationContextData {
  tabsBar: Array<TabBar>;
}

const initialConsultationContextData: ConsultationContextData = {
  tabsBar: [],
};

const ConsultationContext = createContext<any>(null);

// Create the context provider component
export const ConsultationProvider: React.FC<Props> = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { translations } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  let isReports = location.pathname.includes('reports');

  const queryParamSalesOrderId = searchParams.get('sales_order_id');
  const queryParamSalesOrderItemId = searchParams.get('sales_order_item_id');
  const { sales_order_id, sales_order_item_id } = useParams();
  const isDirtyState = useAppSelector(selectDirtyState);

  const salesOrderId = queryParamSalesOrderId ?? sales_order_id;
  const salesOrderItemId = queryParamSalesOrderItemId ?? sales_order_item_id;

  const [consultationState, setConsultationState] = useState(initialConsultationContextData);
  const [selectedSalesOrder, setSelectedSalesOrder] = useState<SalesOrderInfo | undefined>(
    undefined
  );
  const [productOrder, setProductOrder] = useState<IDigitalHealthProfile | undefined>(undefined);

  const [workOrderStatusId, setWorkOrderStatusId] = useState<number | undefined>(undefined);
  const [selectedListOrder, setSelectedListOrder] = useState<SalesOrderInfo | undefined | null>(
    null
  );
  const [eyesonGuestToken, setEyesonGuestToken] = useState<string | undefined>();
  const [selectedDoctor, setSelectedDoctor] = useState<IDoctor>();

  const [finding, setFinding] = useState<ConsultationFinding>();
  const [findingState, setFindingState] = useState<EditorState>(EditorState.createEmpty());

  const [recommendation, setRecommendation] = useState<ConsultationRecommendation>();
  const [recommendationState, setRecommendationState] = useState<EditorState>(
    EditorState.createEmpty()
  );
  const [internalNotes, setInternalNotes] = useState<ConsultationNote>();
  const [internalNotesState, setInternalNotesState] = useState<EditorState>(
    EditorState.createEmpty()
  );

  const dirtyState = useAppSelector(selectDirtyState);
  const [sickLeaveState, setSickLeaveState] = useState<string>('');
  const [prescriptionState, setPrescriptionState] = useState<string>('');

  const [investigation, setInvestigation] = useState<ConsultationInvestigation>();

  const salesOrders: SalesOrderInfo[] = useAppSelector<SalesOrderInfo[]>(selectSalesOrders);
  const salesOrdersStatus: string = useAppSelector<string>(selectSalesOrdersStatus);
  const userRole: string = useAppSelector<string>(selectUserRole);

  const createdWorkOrderService: IWorkOrderService | null =
    useAppSelector<IWorkOrderService | null>(selectCreatedWorkOrderService);
  const consultationFindings: ConsultationFinding[] = useAppSelector<ConsultationFinding[]>(
    selectConsultationFindings
  );
  const consultationFindingsStatus: string = useAppSelector<string>(
    selectConsultationFindingStatus
  );
  const consultationRecommendations: ConsultationRecommendation[] = useAppSelector<
    ConsultationRecommendation[]
  >(selectConsultationRecommendations);
  const consultationRecommendationsStatus: string = useAppSelector<string>(
    selectConsultationRecommendationStatus
  );
  const consultationNotesStatus = useAppSelector<string>(selectConsultationNoteStatus);
  const consultationNotes = useAppSelector<ConsultationNote[]>(selectConsultationNotes);
  const businessPartnerId = useAppSelector<string>(selectBusinessPartnerId);
  const personLoggedIn = useAppSelector<IPerson | undefined>(selectPersonLoggedIn);
  const userLocation: string = useAppSelector<string>(selectUserLocation);

  const consultationInvestigationsStatus: string = useAppSelector<string>(
    selectConsultationInvestigationStatus
  );
  const consultationInvestigations: ConsultationInvestigation[] = useAppSelector<
    ConsultationInvestigation[]
  >(selectConsultationInvestigations);
  const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  const doctors: IDoctor[] = useAppSelector<IDoctor[]>(selectDoctors);

  const customerMenu = useMemo(() => {
    let menu = [
      {
        value: 0,
        text: 'customer.profile',
        isVisible: true,
      },
      {
        value: 1,
        text: 'appointment.details',
        isVisible: true,
      },
      {
        value: 2,
        text: 'appointment.doctor',
        isVisible: true,
      },
      {
        value: 3,
        text: 'digital.health.profile',
        isVisible: true,
      },
      {
        value: 16,
        text: translations('anamnesis'),
        isVisible: true,
      },
    ];
    if (workOrderStatusId === undefined) {
      menu = menu.filter((m) => ![16].includes(m.value));
    }
    if (workOrderStatusId === 2) {
      menu = menu.filter((m) => ![16].includes(m.value));
    }
    return menu;
  }, [userRole, workOrderStatusId]);

  const doctorMenu = useMemo(() => {
    let menu = [
      {
        value: 5,
        text: translations('findings'),
        isVisible: true,
      },
      {
        value: 6,
        text: translations('recommendations'),
        isVisible: true,
      },
      {
        value: 7,
        text: 'documents',
        isVisible: true,
      },
      {
        value: 8,
        text: 'internal.notes',
        isVisible: true,
      },
      {
        value: 9,
        text: 'vital.signs',
        isVisible: true,
      },
      {
        value: 10,
        text: 'ultrasound',
        isVisible: true,
      },
      {
        value: 11,
        text: 'examinations',
        isVisible: true,
      },
      {
        value: 12,
        text: translations('additional.services'),
        isVisible: true,
      },
      {
        value: 14,
        text: translations('prescription'),
        isVisible: true,
      },
      {
        value: 15,
        text: translations('sick_leave'),
        isVisible: true,
      },
      {
        value: 17,
        text: translations('preview'),
        isVisible: true,
      },
    ];
    if (!accountInfo.performs_vital_signs) {
      menu = menu.filter((m) => m.value !== 9);
    }
    if (!accountInfo.performs_ecg) {
      menu = menu.filter((m) => m.value !== 10);
    }
    if (!accountInfo.performs_examinations) {
      menu = menu.filter((m) => m.value !== 11);
    }
    if (!accountInfo.performs_other_services) {
      menu = menu.filter((m) => m.value !== 12);
    }
    if (!accountInfo.performs_sick_leave) {
      menu = menu.filter((m) => m.value !== 15);
    }
    if (!accountInfo.performs_sick_leave) {
      menu = menu.filter((m) => m.value !== 14);
    }
    // if (
    //   selectedSalesOrder?.service_service_group.service_group_id !==
    //   ConsultationTypes.FACE_TO_FACE_CONSULTATION
    // ) {
    //   menu = menu.filter((m) => m.value !== 12);
    // }
    // if (
    //   selectedListOrder?.service_service_group.service_group_id !==
    //   ConsultationTypes.REVIEW_CONSULTATION
    // ) {
    //   menu = menu.filter((m) => m.value !== 12);
    // }
    if (
      workOrderStatusId === 1 &&
      userRole !== NURSE_USER_ROLE &&
      !(
        process.env.REACT_APP_IN_CLINIC_VIDEO_CONSULTATION_SERVICE_ID ===
          selectedSalesOrder?.service.service_id.toString() &&
        selectedSalesOrder?.product.external_product_id.toString() ===
          process.env.REACT_APP_IN_CLINIC_VIDEO_CONSULTATION_PRODUCT_EXTERNAL_ID
      )
    ) {
      menu = menu.filter((m) => m.value !== 12);
    }
    if (workOrderStatusId === undefined) menu = [];
    if (workOrderStatusId === 1 || workOrderStatusId === 5) {
      menu = menu.filter((m) => ![5, 6, 8, 14, 15, 16].includes(m.value));
    }
    return menu;
  }, [
    selectedSalesOrder?.service_service_group.service_group_id,
    userRole,
    workOrderStatusId,
    accountInfo,
  ]);

  useEffect(() => {
    if (consultationFindingsStatus === 'fulfilled') {
      setFinding(
        consultationFindings.find(
          (cf) => cf.consultation_findings_id == createdWorkOrderService?.consultation_findings_id
        )
      );
    }
  }, [consultationFindingsStatus]);

  useEffect(() => {
    if (consultationRecommendationsStatus === 'fulfilled') {
      setRecommendation(
        consultationRecommendations.find(
          (cr) =>
            cr.consultation_recommendations_id ===
            createdWorkOrderService?.consultation_recommendations_id
        )
      );
    }
  }, [consultationRecommendationsStatus]);

  useEffect(() => {
    if (consultationNotesStatus === 'fulfilled') {
      setInternalNotes(
        consultationNotes.find(
          (cf) => cf.consultation_notes_id === createdWorkOrderService?.consultation_notes_id
        )
      );
    }
  }, [consultationNotesStatus]);

  useEffect(() => {
    const tempoDoctorAssigned = doctors.find((doctor) =>
      doctor.person_appointments.find((appointment) => {
        if (salesOrderId && salesOrderItemId) {
          return (
            appointment.sales_order_id == parseInt(salesOrderId) &&
            appointment.sales_order_item_id == parseInt(salesOrderItemId)
          );
        }
        return (
          appointment.sales_order_id == selectedListOrder?.sales_order.sales_order_id &&
          appointment.sales_order_item_id == selectedListOrder?.sales_order_item.sales_order_item_id
        );
      })
    );
    if (tempoDoctorAssigned) {
      setSelectedDoctor(tempoDoctorAssigned);
    } else setSelectedDoctor(undefined);
  }, [doctors, selectedListOrder, salesOrderId, salesOrderItemId]);

  useEffect(() => {
    if (consultationInvestigationsStatus === 'fulfilled') {
      setInvestigation(
        consultationInvestigations.find(
          (ci) =>
            ci.consultation_investigations_id ===
            createdWorkOrderService?.consultation_investigations_id
        )
      );
    }
  }, [consultationInvestigationsStatus]);

  useEffect(() => {
    dispatch(fetchSalesOrders());
    dispatch(fetchDoctors());
  }, [dispatch]);

  useEffect(() => {
    if (salesOrderId && salesOrderItemId && salesOrders.length > 0) {
      const selectedSalesOrder = salesOrders.find(
        (salesOrder) =>
          salesOrder.sales_order.sales_order_id === parseInt(salesOrderId) &&
          salesOrder.sales_order_item.sales_order_item_id === parseInt(salesOrderItemId)
      );
      setSelectedSalesOrder(selectedSalesOrder);
      if (selectedSalesOrder?.work_order?.work_order_status_id !== undefined) {
        dispatch(
          createWorkOrderService({
            serviceId: selectedSalesOrder?.service.service_id,
            salesOrderItemId: selectedSalesOrder?.sales_order_item?.sales_order_item_id,
            salesOrderId: selectedSalesOrder?.sales_order.sales_order_id,
            personId: selectedSalesOrder?.sales_order.business_person_id,
            businessPartnerId: businessPartnerId,
          })
        );
      }
      setWorkOrderStatusId(selectedSalesOrder?.work_order?.work_order_status_id);
      dispatch(fetchConsultationInvestigations());

      const checkProduct = async () => {
        const productFound = await findProductWithTaIdAndTransactionId(selectedSalesOrder);
        if (productFound) setProductOrder(productFound);
      };
      checkProduct();
    }
  }, [salesOrders.length > 0, salesOrderId, salesOrderItemId]);

  useEffect(() => {
    if (salesOrderId && salesOrderItemId && salesOrders.length > 0) {
      const selectedSalesOrder = salesOrders.find(
        (salesOrder) =>
          salesOrder.sales_order.sales_order_id === parseInt(salesOrderId) &&
          salesOrder.sales_order_item.sales_order_item_id === parseInt(salesOrderItemId)
      );
      setSelectedSalesOrder(selectedSalesOrder);
    }
  }, [salesOrdersStatus === 'fulfilled']);

  const closeConsultation = (customerNotJoined: boolean) => {
    if (selectedSalesOrder !== undefined) {
      dispatch(
        updateStatusOfConsultation({
          workOrderId: selectedSalesOrder?.work_order?.work_order_id ?? 0,
          workOrderStatusId: 2,
          details: {
            online_consultation_result: {
              doctor_name: selectedDoctor
                ? `${selectedDoctor?.person_firstname} ${selectedDoctor?.person_lastname}`
                : userLocation,
              findings:
                consultationFindings.find(
                  (cf: any) =>
                    cf.consultation_findings_id ===
                    createdWorkOrderService?.consultation_findings_id
                )?.consultation_findings_description ?? '',
              investigation: investigation?.consultation_investigations_description ?? '',
              recommandation: recommendation?.consultation_recommendations_description ?? '',
            },
          },
          taId: selectedSalesOrder?.sales_order.ta_id ?? 0,
          transactionId: selectedSalesOrder?.sales_order.transaction_id ?? 0,
          salesOrderExternalId: selectedSalesOrder?.sales_order.sales_order_external_id,
          salesOrderId: selectedSalesOrder?.sales_order.sales_order_id,
          externalSalesOrderItemId:
            selectedSalesOrder?.sales_order_item?.external_sales_order_item_id,
          personEmail: selectedSalesOrder?.customer_person.person_email,
          personAccountNumber: selectedSalesOrder?.customer_person.person_account_number ?? '',
          merchantPersonId: personLoggedIn?.person_external_id,
          doctor: selectedDoctor
            ? `${selectedDoctor.person_firstname} ${selectedDoctor.person_lastname}`
            : undefined,
          isFaceToFace:
            ConsultationTypes.FACE_TO_FACE_CONSULTATION ===
              Number(selectedSalesOrder.service_service_group.service_group_id) ||
            ConsultationTypes.REVIEW_CONSULTATION ===
              Number(selectedSalesOrder.service_service_group.service_group_id),

          appointmentStatus: customerNotJoined ? 'M' : 'A',
        })
      );
    }
    setWorkOrderStatusId(2);
    if (isReports) {
      navigate('/web/reports');
    } else {
      navigate('/web/schedule');
    }
    return;
  };

  const stateHandler = (key: string, state: string) => {
    setConsultationState((prevState) => ({ ...prevState, [key]: state }));
  };

  const addTab = (newTab: TabBar) => {
    const existingTab = consultationState.tabsBar.find((t: TabBar) => t.value === newTab.value);
    if (existingTab) {
      setConsultationState((prevState: any) => ({
        ...prevState,
        tabsBar: prevState.tabsBar.map((t: TabBar) => {
          if (t.value === existingTab.value) {
            return { ...t, isVisible: true };
          }
          return { ...t, isVisible: false };
        }),
      }));
    } else {
      setConsultationState((prevState) => ({
        ...prevState,
        tabsBar: [...prevState.tabsBar.map((t: TabBar) => ({ ...t, isVisible: false })), newTab],
      }));
    }
  };

  const deleteTab = (e: any, tab: TabBar, isDirty: boolean) => {
    e.stopPropagation();
    if ([5, 6, 8].includes(tab.value)) {
      const tabName: any = {
        5: () => setFindingState(EditorState.createEmpty()),
        6: () => setRecommendationState(EditorState.createEmpty()),
        8: () => setInternalNotesState(EditorState.createEmpty()),
      };
      tabName[tab.value]();
    }
    if ([7, 14, 15, 16].includes(tab.value) && (isDirty ?? dirtyState.isDirty)) {
      dispatch(
        setDirtyState({
          isDirty: true,
          isModalOpen: true,
          component: isDirtyState.component,
        })
      );

      return;
    }

    setConsultationState((prevState) => {
      const filteredData = prevState.tabsBar.filter((t: TabBar) => t.value !== tab.value);
      if (filteredData.length > 0) {
        filteredData[filteredData.length - 1].isVisible = true;
      }
      return {
        ...prevState,
        tabsBar: filteredData,
      };
    });
  };

  const clearTabs = () => setConsultationState((prevState) => ({ ...prevState, tabsBar: [] }));

  const activateTab = (tab: TabBar) => {
    setConsultationState((prevState) => ({
      ...prevState,
      tabsBar: prevState.tabsBar.map((t: TabBar) => {
        if (t.value === tab.value) {
          return { ...t, isVisible: true };
        }
        return { ...t, isVisible: false };
      }),
    }));
  };

  return (
    <ConsultationContext.Provider
      value={{
        consultationState,
        stateHandler,
        selectedSalesOrder,
        customerMenu,
        doctorMenu,
        addTab,
        deleteTab,
        activateTab,
        clearTabs,
        finding,
        consultationFindings,
        createdWorkOrderService,
        investigation,
        recommendation,
        internalNotes,
        eyesonGuestToken,
        setEyesonGuestToken,
        setSelectedListOrder,
        selectedListOrder,
        setWorkOrderStatusId,
        workOrderStatusId,
        selectedDoctor,
        findingState,
        recommendationState,
        internalNotesState,
        setFindingState,
        setRecommendationState,
        setInternalNotesState,
        sickLeaveState,
        setSickLeaveState,
        prescriptionState,
        setPrescriptionState,
        closeConsultation,
        isReports,
        productOrder,
      }}
    >
      {children}
    </ConsultationContext.Provider>
  );
};

export const useConsultationContext = () => useContext(ConsultationContext);
